import { useEffect, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import {
    Alert,
    Autocomplete,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    useTheme,
    IconButton,
    Modal,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import { request, error400Handler, error422Handler } from "@/Api";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import { CustomBox } from "@/common/StyledComponents";
import { Heading } from "@/common/typography";

const WEBHOOK_EVENTS = [
    "*",
    "client.created",
    "client.updated",
    "client.deleted",
    "project.created",
    "project.updated",
    "project.deleted",
    "forecast_bill.created",
    "forecast_bill.updated",
    "forecast_bill.deleted",
];

// Fields that map to a specific input in the webhook form. Any 422 error whose
// target isn't one of these is surfaced in the general error banner instead.
const WEBHOOK_FORM_FIELDS = ["url", "events"];

export default function Webhooks() {
    const [webhooks, setWebhooks] = useState([]);
    const [activeWebhook, setActiveWebhook] = useState(null);
    const [webhookPopupOpen, setWebhookPopupOpen] = useState(false);
    const [confirmDeleteWebhookOpen, setConfirmDeleteWebhookOpen] =
        useState(false);
    const [newWebhookSecret, setNewWebhookSecret] = useState(null);
    const [deliveriesWebhook, setDeliveriesWebhook] = useState(null);

    const getWebhooks = () => {
        request.get("webhooks").then((response) => {
            setWebhooks(response.data);
        });
    };

    const handleEditWebhook = (webhook) => {
        setActiveWebhook(webhook);
        setWebhookPopupOpen(true);
    };

    const handleDeleteWebhook = (webhook) => {
        setActiveWebhook(webhook);
        setConfirmDeleteWebhookOpen(true);
    };

    const deleteWebhook = () => {
        request.delete(`/webhooks/${activeWebhook.id}`).then(() => {
            setWebhooks((prev) =>
                prev.filter((w) => w.id !== activeWebhook.id),
            );
        });
    };

    const handleToggleWebhookActive = (webhook) => () => {
        request
            .put(`/webhooks/${webhook.id}`, {
                is_active: !webhook.is_active,
            })
            .then((response) => {
                setWebhooks(
                    webhooks.map((w) =>
                        w.id === response.data.id ? response.data : w,
                    ),
                );
            });
    };

    useEffect(() => {
        getWebhooks();
    }, []);

    return (
        <Grid
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={2}
        >
            <Grid item alignSelf="flex-end">
                <Button
                    variant="contained"
                    onClick={() => {
                        setActiveWebhook(null);
                        setWebhookPopupOpen(true);
                    }}
                >
                    Add
                </Button>
            </Grid>

            <Grid item flexGrow={1}>
                <Table style={{ width: "100%" }} width="100%">
                    <TableBody width="100%">
                        <TableRow>
                            <TableCell>URL</TableCell>
                            <TableCell>Events</TableCell>
                            <TableCell>Active</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                        {webhooks.map((webhook) => (
                            <TableRow key={webhook.id}>
                                <TableCell
                                    sx={{
                                        wordBreak: "break-all",
                                        maxWidth: 320,
                                    }}
                                >
                                    {webhook.url}
                                </TableCell>
                                <TableCell>
                                    {(webhook.events || []).map((event) => (
                                        <Chip
                                            key={event}
                                            label={event}
                                            size="small"
                                            sx={{ margin: "2px" }}
                                        />
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        checked={!!webhook.is_active}
                                        onChange={handleToggleWebhookActive(
                                            webhook,
                                        )}
                                    />
                                </TableCell>
                                <TableCell width={150}>
                                    <IconButton
                                        title="Deliveries"
                                        onClick={() =>
                                            setDeliveriesWebhook(webhook)
                                        }
                                    >
                                        <HistoryIcon />
                                    </IconButton>
                                    <IconButton
                                        title="Edit"
                                        onClick={() =>
                                            handleEditWebhook(webhook)
                                        }
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        title="Delete"
                                        onClick={() =>
                                            handleDeleteWebhook(webhook)
                                        }
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Grid>

            <WebhookPopup
                open={webhookPopupOpen}
                closePopup={() => {
                    setWebhookPopupOpen(false);
                    setActiveWebhook(null);
                }}
                webhooks={webhooks}
                setWebhooks={setWebhooks}
                activeWebhook={activeWebhook}
                onSecretRevealed={setNewWebhookSecret}
            />
            <ConfirmationDialog
                open={confirmDeleteWebhookOpen}
                closeDialog={() => setConfirmDeleteWebhookOpen(false)}
                title="Are you sure?"
                callback={() => {
                    if (activeWebhook) {
                        deleteWebhook();
                    }
                }}
            >
                {`This will delete the webhook "${activeWebhook ? activeWebhook.url : ""}".`}
            </ConfirmationDialog>
            <WebhookSecretDialog
                secret={newWebhookSecret}
                closeDialog={() => setNewWebhookSecret(null)}
            />
            <WebhookDeliveriesModal
                webhook={deliveriesWebhook}
                closeModal={() => setDeliveriesWebhook(null)}
            />
        </Grid>
    );
}

const WebhookPopup = ({
    open,
    closePopup,
    setWebhooks,
    webhooks,
    activeWebhook,
    onSecretRevealed,
}) => {
    const theme = useTheme();
    const defaultWebhook = {
        url: "",
        events: [],
        is_active: true,
    };
    const [webhook, setWebhook] = useState({ ...defaultWebhook });
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState(null);

    const handleInputChange = (field) => (event) => {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        setWebhook({ ...webhook, [field]: event.target.value });
    };

    const handleError = (err) => {
        error422Handler(err, (parsedError) => {
            const fieldErrors = {};
            const otherMessages = [];
            Object.entries(parsedError).forEach(([field, msg]) => {
                if (WEBHOOK_FORM_FIELDS.includes(field)) {
                    fieldErrors[field] = msg;
                } else {
                    otherMessages.push(msg);
                }
            });
            setErrors(fieldErrors);
            if (otherMessages.length) {
                setGeneralError(otherMessages.join(" "));
            }
        });
        error400Handler(err, (detail) => {
            setGeneralError(detail);
        });
        if (
            !err.response ||
            (err.response.status !== 422 && err.response.status !== 400)
        ) {
            setGeneralError("Something went wrong. Please try again.");
        }
    };

    const addWebhook = () => {
        setErrors({});
        setGeneralError(null);
        request
            .post(`/webhooks`, {
                url: webhook.url,
                events: webhook.events,
            })
            .then((response) => {
                closePopup();
                setWebhook({ ...defaultWebhook });
                setWebhooks((prev) => [...prev, response.data]);
                if (response.data.secret) {
                    onSecretRevealed(response.data.secret);
                }
            })
            .catch(handleError);
    };

    const updateWebhook = () => {
        setErrors({});
        setGeneralError(null);
        request
            .put(`/webhooks/${activeWebhook.id}`, {
                url: webhook.url,
                events: webhook.events,
                is_active: webhook.is_active,
            })
            .then((response) => {
                closePopup();
                setWebhook({ ...defaultWebhook });
                setWebhooks((prev) =>
                    prev.map((w) =>
                        w.id === response.data.id ? response.data : w,
                    ),
                );
            })
            .catch(handleError);
    };

    useEffect(() => {
        setErrors({});
        setGeneralError(null);
        setWebhook(
            activeWebhook
                ? {
                      ...defaultWebhook,
                      ...activeWebhook,
                      events: activeWebhook.events || [],
                  }
                : { ...defaultWebhook },
        );
    }, [open, activeWebhook]);

    return (
        <Modal open={open} onClose={closePopup}>
            <CustomBox
                style={{
                    background: theme.palette.background.paper,
                    overflow: "auto",
                    minWidth: 600,
                }}
            >
                <Heading>{`${activeWebhook ? "Update" : "Add"} Webhook`}</Heading>
                {generalError && (
                    <Alert severity="error" sx={{ marginTop: 2 }}>
                        {generalError}
                    </Alert>
                )}
                <h3>Endpoint URL</h3>
                <TextField
                    fullWidth
                    placeholder="https://example.com/hooks/makeos"
                    onChange={handleInputChange("url")}
                    value={webhook.url || ""}
                    size="small"
                    error={!!errors.url}
                    helperText={errors.url}
                />
                <h3>Events</h3>
                <Autocomplete
                    multiple
                    options={WEBHOOK_EVENTS}
                    value={webhook.events || []}
                    onChange={(e, value) => {
                        setErrors((prev) => ({ ...prev, events: undefined }));
                        setWebhook({ ...webhook, events: value });
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder="Select events"
                            error={!!errors.events}
                            helperText={errors.events}
                        />
                    )}
                />
                <span style={{ fontSize: 12, lineHeight: 2.5, color: "grey" }}>
                    Use &quot;*&quot; to receive every event.
                </span>
                <Grid
                    container
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: 2 }}
                >
                    <Grid item>
                        <Button
                            variant="contained"
                            onClick={activeWebhook ? updateWebhook : addWebhook}
                            disabled={
                                !webhook.url ||
                                !(webhook.events && webhook.events.length)
                            }
                            sx={{ marginTop: 2 }}
                        >
                            {activeWebhook ? "Update" : "Create"}
                        </Button>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    );
};

const WebhookSecretDialog = ({ secret, closeDialog }) => (
    <Dialog open={!!secret} onClose={closeDialog}>
        <DialogTitle>Webhook created — save your signing secret</DialogTitle>
        <DialogContent dividers>
            <Alert severity="warning" sx={{ marginBottom: 2 }}>
                This is the only time the secret is shown. Store it securely —
                you will need it to verify incoming requests.
            </Alert>
            <Grid container alignItems="center" spacing={1} wrap="nowrap">
                <Grid item flexGrow={1}>
                    <TextField
                        fullWidth
                        value={secret || ""}
                        size="small"
                        InputProps={{
                            readOnly: true,
                            style: { fontFamily: "monospace", fontSize: 13 },
                        }}
                    />
                </Grid>
                <Grid item>
                    <IconButton
                        title="Copy"
                        onClick={() =>
                            navigator.clipboard &&
                            navigator.clipboard.writeText(secret || "")
                        }
                    >
                        <ContentCopyIcon />
                    </IconButton>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={closeDialog} variant="contained">
                Done
            </Button>
        </DialogActions>
    </Dialog>
);

const WebhookDeliveriesModal = ({ webhook, closeModal }) => {
    const theme = useTheme();
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!webhook) {
            setDeliveries([]);
            return;
        }
        setLoading(true);
        request
            .get(`/webhooks/${webhook.id}/deliveries`)
            .then((response) => {
                setDeliveries(response.data);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [webhook]);

    return (
        <Modal open={!!webhook} onClose={closeModal}>
            <CustomBox
                style={{
                    background: theme.palette.background.paper,
                    overflow: "auto",
                    minWidth: 700,
                    maxHeight: "80vh",
                }}
            >
                <Heading>Recent Deliveries</Heading>
                {webhook && (
                    <span style={{ color: "grey", wordBreak: "break-all" }}>
                        {webhook.url}
                    </span>
                )}
                {loading ? (
                    <div style={{ padding: 24, textAlign: "center" }}>
                        <CircularProgress size={30} />
                    </div>
                ) : deliveries.length === 0 ? (
                    <Typography sx={{ marginTop: 2 }}>
                        No delivery attempts recorded.
                    </Typography>
                ) : (
                    <Table style={{ width: "100%" }} width="100%">
                        <TableBody width="100%">
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Result</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell>Attempted</TableCell>
                            </TableRow>
                            {deliveries.map((delivery, index) => (
                                <TableRow key={index}>
                                    <TableCell>{delivery.event}</TableCell>
                                    <TableCell>
                                        {delivery.status_code ?? "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={
                                                delivery.success
                                                    ? "Success"
                                                    : "Failed"
                                            }
                                            color={
                                                delivery.success
                                                    ? "success"
                                                    : "error"
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            wordBreak: "break-word",
                                            maxWidth: 240,
                                        }}
                                    >
                                        {delivery.error_message || "—"}
                                    </TableCell>
                                    <TableCell>
                                        {delivery.created_at
                                            ? new Date(
                                                  delivery.created_at,
                                              ).toLocaleString()
                                            : "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CustomBox>
        </Modal>
    );
};
